// frontend/src/components/games/TriviaGame.jsx
// Trivia Quiz - Fast-paced knowledge competition

import React, { useState, useEffect } from 'react';
import api from '../../utils/api';

const TriviaGame = ({ session, onLeave, onRefresh }) => {
  const [question, setQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [timeLeft, setTimeLeft] = useState(10);
  const [roundNumber, setRoundNumber] = useState(0);
  const [answered, setAnswered] = useState(false);

  const questions = [
    {
      question: "What is the smallest country in the world?",
      options: ["Monaco", "Vatican City", "San Marino", "Liechtenstein"],
      correct: 1,
      category: "Geography"
    },
    {
      question: "Who invented the telephone?",
      options: ["Edison", "Tesla", "Bell", "Marconi"],
      correct: 2,
      category: "History"
    },
    {
      question: "What is the hardest natural substance?",
      options: ["Gold", "Iron", "Diamond", "Platinum"],
      correct: 2,
      category: "Science"
    },
    {
      question: "Which programming language is known as the 'language of the web'?",
      options: ["Python", "JavaScript", "Java", "C++"],
      correct: 1,
      category: "Technology"
    },
    {
      question: "What is the largest mammal?",
      options: ["Elephant", "Blue Whale", "Giraffe", "Rhino"],
      correct: 1,
      category: "Nature"
    },
    {
      question: "In which year did the Titanic sink?",
      options: ["1910", "1911", "1912", "1913"],
      correct: 2,
      category: "History"
    },
    {
      question: "What is the speed of light?",
      options: ["299,792 km/s", "199,792 km/s", "399,792 km/s", "99,792 km/s"],
      correct: 0,
      category: "Science"
    },
    {
      question: "Who painted 'The Starry Night'?",
      options: ["Picasso", "Van Gogh", "Monet", "Rembrandt"],
      correct: 1,
      category: "Art"
    },
    {
      question: "What is the capital of Australia?",
      options: ["Sydney", "Melbourne", "Canberra", "Brisbane"],
      correct: 2,
      category: "Geography"
    },
    {
      question: "How many bones are in the human body?",
      options: ["186", "206", "226", "246"],
      correct: 1,
      category: "Science"
    }
  ];

  useEffect(() => {
    if (session.status === 'playing' && timeLeft > 0 && !answered) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && !answered && question) {
      handleTimeout();
    }
  }, [timeLeft, session.status, answered]);

  const handleStartGame = async () => {
    try {
      await api.post(`/funzone/session/${session.sessionCode}/start`);
      startNewRound();
      onRefresh();
    } catch (error) {
      console.error('Start error:', error);
      alert(error.response?.data?.message || 'Failed to start game');
    }
  };

  const startNewRound = () => {
    const randomQuestion = questions[Math.floor(Math.random() * questions.length)];
    setQuestion(randomQuestion);
    setSelectedAnswer(null);
    setAnswered(false);
    setTimeLeft(10);
    setRoundNumber(prev => prev + 1);
  };

  const handleAnswer = async (answerIndex) => {
    if (answered) return;
    
    setAnswered(true);
    setSelectedAnswer(answerIndex);
    
    const isCorrect = answerIndex === question.correct;
    
    if (isCorrect) {
      // Calculate points with speed bonus
      const basePoints = 100;
      const speedBonus = Math.floor(timeLeft * 5); // Up to 50 bonus points
      const totalPoints = basePoints + speedBonus;
      
      try {
        await api.put(`/funzone/session/${session.sessionCode}/score`, {
          userId: session.players[0].user._id,
          points: totalPoints
        });
      } catch (error) {
        console.error('Score error:', error);
      }
    }
    
    setTimeout(() => {
      if (roundNumber < 15) {
        startNewRound();
      } else {
        handleFinishGame();
      }
    }, 2500);
  };

  const handleTimeout = () => {
    setAnswered(true);
    setTimeout(() => {
      if (roundNumber < 15) {
        startNewRound();
      } else {
        handleFinishGame();
      }
    }, 2000);
  };

  const handleFinishGame = async () => {
    try {
      await api.post(`/funzone/session/${session.sessionCode}/finish`);
      alert('Quiz finished! Check the leaderboard.');
      onLeave();
    } catch (error) {
      console.error('Finish error:', error);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Geography': 'bg-green-600',
      'History': 'bg-yellow-600',
      'Science': 'bg-blue-600',
      'Technology': 'bg-purple-600',
      'Nature': 'bg-emerald-600',
      'Art': 'bg-pink-600'
    };
    return colors[category] || 'bg-gray-600';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-700 to-pink-600 text-white p-6">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-6">
        <div className="flex justify-between items-center bg-gray-800 rounded-lg p-4">
          <div>
            <h1 className="text-3xl font-bold">🧠 Trivia Quiz</h1>
            <p className="text-gray-400">Session: #{session.sessionCode}</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-500">
                {roundNumber}/15
              </div>
              <div className="text-sm text-gray-400">Questions</div>
            </div>
            <button
              onClick={onLeave}
              className="px-4 py-2 bg-red-600 rounded-lg hover:bg-red-700"
            >
              Leave Quiz
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Leaderboard Sidebar */}
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-xl font-bold mb-4">🏆 Scores</h3>
          <div className="space-y-3">
            {[...session.players]
              .sort((a, b) => b.score - a.score)
              .map((player, idx) => (
                <div key={idx} className="bg-gray-700 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-sm">
                        {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`}
                        {' '}
                        {player.nickname || player.user?.name}
                      </div>
                    </div>
                    <div className="text-yellow-500 font-bold">
                      {player.score}
                    </div>
                  </div>
                </div>
              ))}
          </div>
          
          {session.status === 'waiting' && (
            <button
              onClick={handleStartGame}
              className="w-full mt-6 bg-green-600 text-white py-3 px-4 rounded-lg font-bold hover:bg-green-700"
            >
              Start Quiz
            </button>
          )}
        </div>

        {/* Question Panel */}
        <div className="lg:col-span-3">
          {session.status === 'playing' && question ? (
            <div className="bg-gray-800 rounded-lg p-8">
              {/* Category & Timer */}
              <div className="flex justify-between items-center mb-6">
                <span className={`px-4 py-2 rounded-full text-sm font-bold ${getCategoryColor(question.category)}`}>
                  {question.category}
                </span>
                <div className="flex items-center gap-3">
                  <div className={`text-4xl font-bold ${
                    timeLeft <= 3 ? 'text-red-500 animate-pulse' : 'text-yellow-500'
                  }`}>
                    {timeLeft}s
                  </div>
                </div>
              </div>

              {/* Question */}
              <h2 className="text-3xl font-bold mb-8 text-center">
                {question.question}
              </h2>

              {/* Options */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {question.options.map((option, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleAnswer(idx)}
                    disabled={answered}
                    className={`p-6 rounded-lg text-lg font-bold transition transform hover:scale-105 ${
                      !answered
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : selectedAnswer === idx
                          ? idx === question.correct
                            ? 'bg-green-600 ring-4 ring-green-300'
                            : 'bg-red-600 ring-4 ring-red-300'
                          : idx === question.correct
                            ? 'bg-green-600 ring-4 ring-green-300'
                            : 'bg-gray-600 opacity-50'
                    } disabled:cursor-not-allowed`}
                  >
                    {option}
                    {answered && idx === question.correct && ' ✓'}
                    {selectedAnswer === idx && idx !== question.correct && ' ✗'}
                  </button>
                ))}
              </div>

              {/* Result Message */}
              {answered && (
                <div className="mt-8 text-center">
                  {selectedAnswer === question.correct ? (
                    <div>
                      <p className="text-3xl font-bold text-green-400 mb-2">
                        🎉 Correct!
                      </p>
                      <p className="text-xl text-gray-300">
                        +{100 + Math.floor(timeLeft * 5)} points
                        <span className="text-sm text-gray-400 ml-2">
                          (100 base + {Math.floor(timeLeft * 5)} speed bonus)
                        </span>
                      </p>
                    </div>
                  ) : timeLeft === 0 ? (
                    <p className="text-3xl font-bold text-orange-400">
                      ⏰ Time's up!
                    </p>
                  ) : (
                    <p className="text-3xl font-bold text-red-400">
                      ❌ Wrong answer!
                    </p>
                  )}
                </div>
              )}
            </div>
          ) : session.status === 'waiting' ? (
            <div className="bg-gray-800 rounded-lg p-12 text-center">
              <h2 className="text-4xl font-bold mb-4">🧠 Ready to Test Your Knowledge?</h2>
              <p className="text-xl text-gray-400 mb-8">
                15 questions • Multiple choice • Speed bonus for fast answers!
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-gray-700 rounded-lg p-4">
                  <div className="text-3xl mb-2">🌍</div>
                  <div className="text-sm font-semibold">Geography</div>
                </div>
                <div className="bg-gray-700 rounded-lg p-4">
                  <div className="text-3xl mb-2">📜</div>
                  <div className="text-sm font-semibold">History</div>
                </div>
                <div className="bg-gray-700 rounded-lg p-4">
                  <div className="text-3xl mb-2">🔬</div>
                  <div className="text-sm font-semibold">Science</div>
                </div>
                <div className="bg-gray-700 rounded-lg p-4">
                  <div className="text-3xl mb-2">💻</div>
                  <div className="text-sm font-semibold">Technology</div>
                </div>
                <div className="bg-gray-700 rounded-lg p-4">
                  <div className="text-3xl mb-2">🌿</div>
                  <div className="text-sm font-semibold">Nature</div>
                </div>
                <div className="bg-gray-700 rounded-lg p-4">
                  <div className="text-3xl mb-2">🎨</div>
                  <div className="text-sm font-semibold">Art</div>
                </div>
              </div>

              <div className="bg-blue-900 rounded-lg p-6 mb-6">
                <h3 className="text-xl font-bold mb-3">How to Play:</h3>
                <ul className="text-left space-y-2 text-gray-300">
                  <li>✓ Answer questions as fast as possible</li>
                  <li>✓ Correct answer = 100 points + speed bonus</li>
                  <li>✓ Speed bonus = up to 50 points (faster = more)</li>
                  <li>✓ 10 seconds per question</li>
                  <li>✓ Highest score wins!</li>
                </ul>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default TriviaGame;
